'use client'

import { useEffect, useState, useRef } from 'react'
import { init } from '@amplitude/analytics-browser'
import { getCookieConsent } from '@/components/Cookie'

const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY

export function AmplitudeInitializer({ isProduction }: { isProduction: boolean }) {
  const [hasConsent, setHasConsent] = useState(false)
  const hasInitialized = useRef(false)

  useEffect(() => {
    const consent = getCookieConsent()
    setHasConsent(consent === 'accepted')

    // Listen for consent changes
    const handleConsentChange = (event: CustomEvent) => {
      const newConsent = event.detail.consent
      setHasConsent(newConsent === 'accepted')
    }

    window.addEventListener('cookieConsentChanged', handleConsentChange as EventListener)

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange as EventListener)
    }
  }, [])

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) {
      return
    }

    if (!AMPLITUDE_API_KEY && isProduction) {
      console.error('❌ Amplitude API key is missing')
      return
    }

    if (!isProduction) {
      console.info('🚫 Amplitude is disabled in development')
      return
    }

    if (!hasConsent) {
      console.info('🚫 Amplitude is disabled - no cookie consent')
      return
    }

    try {
      init(AMPLITUDE_API_KEY!, undefined, {
        autocapture: true,
        defaultTracking: {
          sessions: true,
          pageViews: true,
        },
        serverZone: 'EU',
      })
      hasInitialized.current = true
      console.info('✅ Amplitude initialized successfully')
    } catch (error) {
      console.error('❌ Failed to initialize Amplitude:', error)
    }
  }, [isProduction, hasConsent])

  return null // This component doesn't render anything
}
